import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { FileText, Loader2, LogOut } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Blocking acceptance of the Infinity Touch terms.
 *
 * Rendered as an early return from ResellerDashboard rather than as a dialog.
 * A shadcn Dialog closes on Escape and on an outside click, and making one
 * genuinely un-closable is work that then has to keep working. Returning early
 * from the shared shell covers every /reseller/* deep link by construction, and
 * no reseller page mounts or fires its data fetches behind it.
 *
 * Whether the gate applies at all is decided by the server
 * (GET /stores/my/terms-status); this component only renders and submits.
 */
export default function TermsGate({ status, onAccepted, onLogout }) {
    const [page, setPage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [agreed, setAgreed] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [scrolledToEnd, setScrolledToEnd] = useState(false);
    const scrollRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        axios.get(`${API}/pages/public/${status.slug}`)
            .then((res) => { if (!cancelled) setPage(res.data); })
            .catch(() => { if (!cancelled) toast.error('Could not load the terms & conditions.'); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [status.slug]);

    // Short documents may not scroll at all; treat those as already read rather
    // than leaving the checkbox permanently disabled.
    useEffect(() => {
        const el = scrollRef.current;
        if (el && el.scrollHeight <= el.clientHeight + 8) setScrolledToEnd(true);
    }, [page]);

    const handleScroll = (e) => {
        const el = e.currentTarget;
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) setScrolledToEnd(true);
    };

    const accept = async () => {
        setSubmitting(true);
        try {
            await axios.post(`${API}/stores/my/terms-acceptance`, {
                version: status.current_version,
                source: 'web'
            });
            onAccepted();
        } catch (error) {
            // 409 means the text changed under us — reload so the person accepts
            // what is actually current rather than what they were shown.
            if (error.response?.status === 409) {
                toast.error('These terms were just updated. Reloading the latest version.');
                window.location.reload();
                return;
            }
            toast.error(error.response?.data?.message || 'Could not record your acceptance.');
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
            <div className="card-elevated w-full max-w-3xl flex flex-col max-h-[92vh]">
                <div className="p-6 border-b border-slate-100">
                    <div className="flex items-center gap-3">
                        <div className="w-11 h-11 bg-indigo-100 rounded-xl flex items-center justify-center shrink-0">
                            <FileText className="w-5 h-5 text-indigo-600" />
                        </div>
                        <div>
                            <h1 className="text-xl font-bold text-slate-900">{page?.title || 'Terms & Conditions'}</h1>
                            <p className="text-sm text-slate-500">
                                Please read and accept these terms to continue using Infinity Touch.
                            </p>
                        </div>
                    </div>
                </div>

                <div
                    ref={scrollRef}
                    onScroll={handleScroll}
                    className="flex-1 overflow-y-auto p-6 custom-scrollbar"
                >
                    {loading ? (
                        <div className="flex items-center justify-center py-16 text-slate-400">
                            <Loader2 className="w-6 h-6 animate-spin" />
                        </div>
                    ) : page ? (
                        // Same treatment the public page already gives this stored
                        // HTML; it is admin-authored content, not user input.
                        <div
                            className="quill-content prose prose-slate max-w-none text-sm"
                            dangerouslySetInnerHTML={{ __html: page.content }}
                        />
                    ) : (
                        <p className="text-sm text-slate-500">
                            The terms could not be loaded. Please refresh the page, or contact support if this continues.
                        </p>
                    )}
                </div>

                <div className="p-6 border-t border-slate-100 space-y-4">
                    <label className="flex items-start gap-3 cursor-pointer">
                        <Checkbox
                            checked={agreed}
                            onCheckedChange={(v) => setAgreed(v === true)}
                            disabled={!page || !scrolledToEnd}
                            className="mt-0.5"
                            data-testid="terms-agree-checkbox"
                        />
                        <span className="text-sm text-slate-700">
                            I have read and agree to the Terms &amp; Conditions on behalf of my funeral home.
                            {!scrolledToEnd && page && (
                                <span className="block text-xs text-slate-400 mt-0.5">
                                    Scroll to the end of the terms to continue.
                                </span>
                            )}
                        </span>
                    </label>

                    <div className="flex items-center justify-between gap-3">
                        <Button
                            variant="ghost"
                            onClick={onLogout}
                            className="text-slate-500 hover:text-slate-700"
                        >
                            <LogOut className="w-4 h-4 mr-2" />
                            Sign out
                        </Button>
                        <Button
                            onClick={accept}
                            disabled={!agreed || submitting}
                            className="bg-indigo-600 hover:bg-indigo-700 rounded-full px-8"
                            data-testid="terms-accept-btn"
                        >
                            {submitting ? 'Saving…' : 'Accept and continue'}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}
